import { toLocalTime } from '../filterTimeControl/toLocalTime';
import { parseTestDate } from '../timeUtils/timeUtils';

/**
 * Checks if a date falls within a specified validity range.
 *
 * @param validFrom - The start date for the validity range. If provided, the date to compare should be equal to or later than this date. If not provided or null, there is no lower limit.
 * @param validTo - The end date for the validity range. If provided, the date to compare should be equal to or earlier than this date. If not provided or null, there is no upper limit.
 * @param testDate - An optional date to check for validity. If not specified, the current date and time are used.
 *
 * @returns {boolean} - Returns true if the specified date (or the current date if not specified) falls within the validity range. Returns true if both `validFrom` and `validTo` are null or unspecified.
 */
export const dateCompare = ({
  validFrom,
  validTo,
  testDate,
}: {
  validFrom?: string | null;
  validTo?: string | null;
  testDate?: string | null;
}): boolean => {
  // Fail early: If both validFrom and validTo are null or undefined, return true immediately
  if (!validFrom && !validTo) {
    return true;
  }

  const currentDate = parseTestDate({ testDate }) || toLocalTime(new Date());
  if (!currentDate) {
    return false;
  }
  const compareVal = currentDate?.getTime();

  if (validFrom) {
    const validFromVal = toLocalTime(new Date(validFrom))?.getTime();
    if (validFromVal === undefined || Number.isNaN(validFromVal) || compareVal < validFromVal) {
      return false;
    }
  }

  if (validTo) {
    const validToVal = toLocalTime(new Date(validTo))?.getTime();
    if (validToVal === undefined || Number.isNaN(validToVal) || compareVal > validToVal) {
      return false;
    }
  }

  return true;
};
